<!-- eslint-disable max-len -->
<template>
  <div class="mx-auto min-h-full">
    <!-- No close button: "Minhas integrações" is a destination the seller
         navigated to, not an overlay over something else. The X sent her to
         the dashboard, which the breadcrumb already does — and more legibly. -->
    <SimpleHeader
      :show-close="false"
      :pages="[
        { name: t('sidemenu.navigation.integration.title'), href: router.resolve({ name: 'PluginsManagerPage' }).href, current: true },
      ]"
    />
    <RouterView
      :plugins="plugins"
      :data-loaded="dataLoaded"
      :is-loading-more="isLoadingMore"
      :has-more-pages="hasMorePages"
      :total-count="totalCount"
      @load-more="loadMorePlugins"
      @search="onSearch"
      @sort="onSort"
    />
  </div>
  <SimpleFooter
    :horizontal="true"
  />
</template>

<script setup lang="ts">
import {
  ref,
  watch,
  onBeforeMount,
} from 'vue';
import { useRouter } from 'vue-router';
import usePluginStore from '@/store/plugin/plugin';
import InstagramLiveMetadata from '@/models/plugin/instagramLiveMetadata';
import Plugin from '@/models/plugin/plugin';
import SimpleHeader from '@/components/tailwind/header/SimpleHeader.vue';
import { useI18n } from 'vue-i18n';
import SimpleFooter from '@/components/tailwind/footer/SimpleFooter.vue';

const router = useRouter();
const { t } = useI18n();

const {
  pluginsDoGetMy,
  pluginGetSharedWithMeLiveMetadatas,
} = usePluginStore();

/*
  Data
*/
const dataLoaded = ref(false);
const isLoading = ref(true);
const isLoadingMore = ref(false);
const plugins = ref([] as Plugin[]);
const newPlugins = ref([] as Plugin[]);
const metadatasSharedWithMe = ref([] as InstagramLiveMetadata[]);
const currentPage = ref(1);
const hasMorePages = ref(false);
// Server-side account-handle search term. Empty = full list. Drives every
// fetch (page 1 + load-more) so the search runs on the backend instead of
// filtering the whole loaded list client-side.
const searchTerm = ref('');
// Server-side sort state. Defaults mirror the view's toolbar (username A→Z)
// so the first page already holds the correct top rows — with pagination the
// backend decides WHICH plugins are on page 1, so a mismatch here would show
// the wrong subset, not merely the right subset in the wrong order.
const orderBy = ref('name');
const order = ref(1);
// Total matches reported by the backend for the current query — lets the
// screen show "N de M resultados" without loading everything.
const totalCount = ref(0);
const PAGE_LIMIT = 100;

/*
  Methods
*/
const redirectDashboard = ((error: string) => router.push({
  name: 'DashboardPage',
  query: {
    message: 'Não foi possível carregar a página. Tente novamente em instantes.',
    detail: error,
    type: 'error',
  },
}));
const loadPlugins = (page: number = 1) => {
  if (page > 1) {
    isLoadingMore.value = true;
  }

  pluginsDoGetMy(
    undefined,
    undefined,
    undefined,
    page,
    PAGE_LIMIT,
    searchTerm.value,
    orderBy.value,
    order.value,
  )
    .then((response) => {
      if (response !== undefined) {
        // Combine Instagram and Facebook plugins
        const allPlugins = [
          ...response.instagramPlugins.map((p: any) => new Plugin(p)),
          ...response.facebookPlugins.map((p: any) => new Plugin(p)),
        ].sort((a, b) => a.name.localeCompare(b.name));

        if (page === 1) {
          plugins.value = allPlugins;
        } else {
          plugins.value = [...plugins.value, ...allPlugins];
        }

        currentPage.value = page;
        hasMorePages.value = response.pagination
          ? response.pagination.currentPage < response.pagination.lastPage
          : false;
        totalCount.value = response.pagination?.total ?? allPlugins.length;
      }
    }).catch((error) => {
      console.error('error', error);
      redirectDashboard(error.toString());
    }).finally(() => {
      isLoadingMore.value = false;
    });
};
const loadMorePlugins = () => {
  if (isLoadingMore.value || !hasMorePages.value) return;
  loadPlugins(currentPage.value + 1);
};
// New search term from the toolbar (already debounced in the view). Reset to
// page 1 so results replace the list instead of appending.
const onSearch = (term: string) => {
  searchTerm.value = term.trim();
  loadPlugins(1);
};
// New sort from the toolbar. Refetch page 1 so the backend returns the
// correct top rows for the chosen field/direction (not just a client
// reorder of whatever happened to be loaded).
const onSort = (payload: { orderBy: string; order: number }) => {
  orderBy.value = payload.orderBy;
  order.value = payload.order;
  loadPlugins(1);
};
const loadMetadatasSharedWithMe = () => {
  pluginGetSharedWithMeLiveMetadatas({
    page: 1,
    limit: 100,
    orderBy: 'createdAt',
    order: -1,
  })
    .then((response) => {
      if (response !== undefined) {
        metadatasSharedWithMe.value = response.data;
      }
    }).catch((error) => {
      console.error('error', error);
      redirectDashboard(error.toString());
    });
};
const load = async () => {
  isLoading.value = true;
  await loadPlugins();
  await loadMetadatasSharedWithMe();

  isLoading.value = false;
  dataLoaded.value = true;
};

/*
  Hooks
*/
onBeforeMount(async () => {
  await load();
});
watch(newPlugins, () => {
  load();
});
</script>
